import { useEffect, useMemo, useState } from "react";
import { styles } from "./stylesConnectWithEmail";

import { Box, Typography, TextField, Button } from "@mui/material";
import LogoBlueWalletModatSvg from "@/assets/svg/LogoBlueWalletModatSvg";
import BackSvg from "@/assets/svg/BackSvg";
import LoaderSvg from "@/assets/svg/LoaderSvg";
import {
  usePasswordResetRequestMutation,
  usePasswordResetResendMutation,
} from "@/store/api/walletApi";

type Props = {
  onBack?: () => void;
  onClose?: () => void;
};

const ForgotPassword = ({ onBack, onClose }: Props) => {
  const [step, setStep] = useState<"email" | "loading" | "after">("email");
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [requestReset, { isLoading }] = usePasswordResetRequestMutation();
  const [resendReset] = usePasswordResetResendMutation();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleBackClick = () => {
    if (step === "after") {
      setStep("email");
    } else if (step === "email" && onBack) {
      onBack();
    }
  };

  const handleNextClick = async () => {
    if (isNextDisabled) return;
    try {
      const res = await requestReset({ email }).unwrap();
      if (res.message?.toLowerCase().includes("reset")) {
        setStep("after");
      } else {
        setEmailError(res.message || "Something went wrong");
      }
    } catch {
      setEmailError("Something went wrong");
    }
  };

  const validateEmail = (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!value) return "Email is required";
    if (!emailRegex.test(value)) return "Invalid email format";
    return "";
  };
  const isNextDisabled = useMemo(() => step !== "email" || !!validateEmail(email), [step, email]);
  const handleResend = async () => {
    try {
      await resendReset({ email }).unwrap();
    } catch (err) {
      console.error("Resend failed", err);
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1400,
        backgroundColor: "rgba(26,29,31,0.40)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onMouseDown={() => onClose?.()}
    >
      <Box sx={styles.wrapper} onClick={onClose} role="dialog" aria-modal="true">
        <Box
          sx={styles.content}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <Box sx={styles.back} onClick={handleBackClick}>
            {onBack && <BackSvg />}
          </Box>
          <Box sx={styles.logoBox}>
            <LogoBlueWalletModatSvg />
          </Box>

          {(step === "loading" || step === "email") && (
            <>
              <Typography sx={styles.title}>Forgot password</Typography>
              <Typography sx={styles.subtitle}>
                Enter the email address associated with your account, and we'll send you a link to
                reset your password.
              </Typography>

              <Box sx={styles.inputWrapper}>
                <Typography sx={styles.label}>Email address</Typography>
                <TextField
                  variant="outlined"
                  placeholder="Enter your Email address"
                  fullWidth
                  value={email}
                  error={!!emailError}
                  helperText=""
                  onChange={(e) => {
                    if (step === "email") {
                      const v = e.target.value;
                      setEmail(v);
                      if (emailTouched) setEmailError(validateEmail(v));
                    }
                  }}
                  onBlur={() => {
                    if (step === "email") {
                      setEmailTouched(true);
                      setEmailError(validateEmail(email));
                    }
                  }}
                  sx={styles.input}
                  InputProps={{
                    sx: styles.inputText,
                    readOnly: step !== "email",
                  }}
                />
              </Box>

              {(step === "loading" || step === "email") && (
                <Button
                  variant="contained"
                  sx={styles.button}
                  onClick={handleNextClick}
                  disabled={!email}
                >
                  Next
                </Button>
              )}
            </>
          )}

          {step === "after" && (
            <>
              <Typography sx={styles.title}>Check your email</Typography>
              <Typography sx={styles.subtitle}>
                We've sent an email with reset password instruction. If haven't received an email
                within few minutes then check your spam folder.
              </Typography>
              <Typography sx={styles.resend}>
                Didn’t receive an email?{" "}
                <Box component="span" sx={styles.resendLink} onClick={handleResend}>
                  Resend
                </Box>
              </Typography>
            </>
          )}
        </Box>

        {isLoading && (
          <Box sx={styles.blurLayer} onClick={(e) => e.stopPropagation()}>
            <Box sx={styles.loaderContainer}>
              <Box sx={styles.loaderIcon}>
                <LoaderSvg />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ForgotPassword;
